import { getAmount } from 'src/tools/reportEngine';
import { presupuestoArs, presupuestoUsd } from 'src/utils/vistaObra/valuation';
import { diasDesde } from './fechas';

export const MONEDAS = ['ARS', 'USD'];
export const MOVIMIENTOS_VISIBLES = 20;

export const otraMoneda = (moneda) => (moneda === 'USD' ? 'ARS' : 'USD');

export const monedaValida = (valor) => (MONEDAS.includes(valor) ? valor : 'ARS');

// Un movimiento sin equivalente guardado cae al monto original: lo resuelve getAmount.
export const montoEn = (mov, moneda) => {
  const valor = Number(getAmount(mov, moneda, 'total'));
  return Number.isFinite(valor) ? valor : 0;
};

export const montosDeMovimiento = (mov) => ({ ARS: montoEn(mov, 'ARS'), USD: montoEn(mov, 'USD') });

export const totalGastado = (movimientos = []) =>
  movimientos
    .filter((m) => m.tipo === 'egreso')
    .reduce((acc, m) => ({ ARS: acc.ARS + montoEn(m, 'ARS'), USD: acc.USD + montoEn(m, 'USD') }), { ARS: 0, USD: 0 });

// Presupuestos en CAC no se convierten a pesos o dólares sin el índice: quedan afuera del total.
export const montosDePresupuesto = (p) => {
  const monto = Number(p?.monto) || 0;
  if (p?.moneda === 'ARS') {
    const usd = presupuestoUsd(p);
    return { ARS: monto, USD: usd > 0 ? usd : null };
  }
  if (p?.moneda === 'USD') return { ARS: presupuestoArs(p), USD: monto };
  return null;
};

// Devuelve null si no hay presupuesto de egreso. Una unidad sin equivalente en algún presupuesto queda en null.
export const totalPresupuestado = (presupuestos = []) => {
  const montos = presupuestos.filter((p) => p.tipo !== 'ingreso').map(montosDePresupuesto).filter(Boolean);
  if (montos.length === 0) return null;
  const sumar = (moneda) => (montos.some((m) => m[moneda] == null) ? null : montos.reduce((n, m) => n + m[moneda], 0));
  return { ARS: sumar('ARS'), USD: sumar('USD') };
};

// Fracción gastado / presupuestado en la unidad principal; null si no se puede calcular.
export const usoDelPresupuesto = (gastado, presupuestado, moneda) => {
  const base = presupuestado?.[moneda];
  if (!base || base <= 0) return null;
  return gastado[moneda] / base;
};

const sumarEn = (movimientos, tipo, moneda) =>
  movimientos.filter((m) => m.tipo === tipo).reduce((acc, m) => acc + montoEn(m, moneda), 0);

export const saldoDeObra = (obra, moneda) => {
  const movimientos = obra?.movimientos || [];
  return sumarEn(movimientos, 'ingreso', moneda) - sumarEn(movimientos, 'egreso', moneda);
};

export const saldoTotal = (obras = [], moneda) => obras.reduce((acc, obra) => acc + saldoDeObra(obra, moneda), 0);

const enAmbas = (calcular) => ({ ARS: calcular('ARS'), USD: calcular('USD') });

export const cajaDeObra = (obra) => {
  const movimientos = obra?.movimientos || [];
  return {
    saldo: enAmbas((m) => saldoDeObra(obra, m)),
    ingresos: enAmbas((m) => sumarEn(movimientos, 'ingreso', m)),
    gastos: enAmbas((m) => sumarEn(movimientos, 'egreso', m)),
    truncado: !!obra?.truncado,
  };
};

export const cajaTotal = (obras = []) => ({
  saldo: enAmbas((m) => saldoTotal(obras, m)),
  parcial: obras.some((o) => o?.truncado),
});

const instante = (fecha) => {
  const t = new Date(fecha).getTime();
  return Number.isFinite(t) ? t : 0;
};

// Mezcla los movimientos de todas las obras, el más reciente primero; cada uno lleva el nombre de su obra.
export const movimientosRecientesDeObras = (obras = [], cantidad = MOVIMIENTOS_VISIBLES) =>
  obras
    .flatMap((obra) => (obra?.movimientos || []).map((m) => ({ ...m, obraNombre: obra.nombre })))
    .sort((a, b) => instante(b.fecha) - instante(a.fecha))
    .slice(0, cantidad);

// «Última carga hoy / ayer / hace N días»; null si la obra no tiene movimientos con fecha.
export const textoUltimaCarga = (obra, ahora = new Date()) => {
  const fechas = (obra?.movimientos || []).map((m) => m.fecha).filter((f) => instante(f) > 0);
  if (fechas.length === 0) return null;
  const dias = diasDesde(fechas.reduce((a, b) => (instante(b) > instante(a) ? b : a)), ahora);
  if (dias === 0) return 'Última carga hoy';
  if (dias === 1) return 'Última carga ayer';
  return `Última carga hace ${dias} días`;
};
